import { useState } from 'react'
import type { BossSqlPreview } from '../../types/boss'

/**
 * The SuperAdmin "View SQL" window: the report's stored query as a designer wrote it, and the
 * same statements with this run's pre-filter values written back in as literals — each in its own
 * textarea with a copy button, so either can go straight into a query tool.
 *
 * Presentational only. The page owns the query (contract §4.2) and hands the answer down, exactly
 * as it does for the pre-filter dialog and the report picker.
 */

/** Copies one block, and says so for a moment. */
function CopyButton({ text, label }: { text: string; label: string }) {
    const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text)
            setState('copied')
        } catch {
            // Clipboard access is refused outside a secure context and in some locked-down
            // browsers. The textarea is still selectable, so say so rather than failing silently.
            setState('failed')
        }
        setTimeout(() => setState('idle'), 1500)
    }

    return (
        <button
            type="button"
            className="btn btn-secondary boss-btn-sm"
            disabled={text === ''}
            aria-label={label}
            onClick={() => { void copy() }}
        >
            {state === 'copied' ? '✓ Copied' : state === 'failed' ? '⚠ Select and copy' : '📋 Copy'}
        </button>
    )
}

/** One labelled, read-only block of SQL. */
function SqlBlock({ title, hint, sql, copyLabel }: {
    title: string; hint: string; sql: string; copyLabel: string
}) {
    return (
        <section className="boss-sql-block">
            <div className="boss-sql-head">
                <div>
                    <div className="boss-sql-title">{title}</div>
                    <div className="boss-hint">{hint}</div>
                </div>
                <CopyButton text={sql} label={copyLabel} />
            </div>
            <textarea
                className="boss-sql-text"
                value={sql}
                readOnly
                spellCheck={false}
                wrap="off"
                aria-label={title}
            />
        </section>
    )
}

export default function SqlDebugDialog({ reportName, preview, loading, error, onClose }: {
    reportName: string
    preview: BossSqlPreview | null
    loading: boolean
    error: string | null
    onClose: () => void
}) {
    // Dimming modal: deliberately no backdrop onClick — dialogs must not close on outside-click.
    return (
        <div className="modal-backdrop">
            <div className="modal boss-sql-dialog">
                <div className="modal-header">
                    <h2>SQL — {reportName}</h2>
                    <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="modal-body boss-sql-body">
                    {loading && <div className="boss-muted">Working out what this report would run…</div>}
                    {error !== null && <div className="boss-error">{error}</div>}
                    {preview && (
                        <>
                            <SqlBlock
                                title="Original report query"
                                hint="The SQL stored on the report, exactly as it was written."
                                sql={preview.originalSql}
                                copyLabel="Copy the original report query"
                            />
                            <SqlBlock
                                title="Resolved query"
                                hint="The same statements with this run’s pre-filter values filled in."
                                sql={preview.resolvedSql}
                                copyLabel="Copy the resolved query"
                            />
                            {preview.appliedFilters.length > 0 && (
                                <div className="boss-sql-note">
                                    <strong>Filters applied:</strong>{' '}
                                    {preview.appliedFilters.map(f => `${f.field} — ${f.description}`).join('; ')}
                                </div>
                            )}
                            {preview.sourceReportId !== preview.reportId && (
                                <div className="boss-sql-note">
                                    This report has no SQL of its own; the query above comes from the
                                    report it links to.
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-primary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    )
}
